import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Input, InputNumber, Select, Space, message, Switch, Tag, Modal, Form, Tooltip, Alert,
} from 'antd';
import {
  PlusOutlined, EditOutlined, SearchOutlined, ClearOutlined, ApiOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall } from '../../utils/helper';
import { getPatientIdLabel } from '../../constants/fieldLabels';
import './masters-config.css';

const { Option } = Select;

// HL7 Forward Routes master: maps a HIS facility code (MSH-4 of the inbound
// HL7 order) to that center's dcm4chee HL7 (MLLP) listener so orders land in
// the right Modality Worklist. The backend picks up changes within ~60s —
// no restart or apply step needed.
const Hl7ForwardRoutes = () => {
  const [routesList, setRoutesList] = useState({ data: [], loading: false, error: null });
  const [centres, setCentres] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  // Changing a live route's issuer re-namespaces PID-3.4, duplicating every
  // existing patient on that archive — so an existing value stays read-only
  // until the admin explicitly confirms they understand that.
  const [issuerUnlocked, setIssuerUnlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [form] = Form.useForm();

  const fetchRoutes = useCallback(async () => {
    setRoutesList(prev => ({ ...prev, loading: true, error: null }));
    try {
      // all=true returns active + inactive so admins can manage everything.
      const response = await makeGetCall('/hl7-forward-routes?all=true');
      if (response?.data?.success) {
        setRoutesList({ data: response.data.data || [], loading: false, error: null });
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch HL7 forward routes');
      }
    } catch (error) {
      setRoutesList({ data: [], loading: false, error: error.message || 'Failed to fetch HL7 forward routes' });
      message.error('Failed to fetch HL7 forward routes');
    }
  }, []);

  const fetchCentres = useCallback(async () => {
    try {
      const res = await makeGetCall('/centres?active=true');
      if (res?.data?.success) {
        setCentres(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch centres:', error);
    }
  }, []);

  useEffect(() => {
    fetchRoutes();
    fetchCentres();
  }, [fetchRoutes, fetchCentres]);

  const centreNameByCode = useMemo(() => {
    const map = {};
    centres.forEach(c => { map[c.code] = c.name; });
    return map;
  }, [centres]);

  const handleAdd = () => {
    setEditingRecord(null);
    setIssuerUnlocked(false);
    form.resetFields();
    form.setFieldsValue({ active: true, port: 2575 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setIssuerUnlocked(false);
    form.setFieldsValue({
      facility_code: record.facility_code,
      centre_code: record.centre_code || undefined,
      host: record.host,
      port: record.port,
      receiving_facility: record.receiving_facility,
      patient_id_issuer: record.patient_id_issuer,
      active: record.active,
      comments: record.comments,
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    setIssuerUnlocked(false);
    form.resetFields();
  };

  const handleUnlockIssuer = () => {
    Modal.confirm({
      title: `Change ${getPatientIdLabel()} Issuer?`,
      content: (
        <span>
          This route is live: its patients are already filed on the worklist server
          under the current issuer. Changing it will make every existing patient on
          that archive appear as a <b>duplicate</b> under the new issuer.
        </span>
      ),
      okText: 'I understand',
      okButtonProps: { danger: true },
      onOk: () => setIssuerUnlocked(true),
    });
  };

  const handleToggleActive = async (record, checked) => {
    setTogglingId(record.id);
    try {
      // The upsert endpoint validates all fields on every call, so echo them back.
      const res = await makePostCall('/hl7-forward-routes', {
        id: record.id,
        facility_code: record.facility_code,
        centre_code: record.centre_code || null,
        host: record.host,
        port: record.port,
        receiving_facility: record.receiving_facility,
        patient_id_issuer: record.patient_id_issuer,
        active: checked,
        comments: record.comments,
      });
      if (res?.data?.success) {
        message.success(res.data.message || `Route ${checked ? 'activated' : 'deactivated'}`);
        fetchRoutes();
      } else {
        throw new Error(res?.data?.message || 'Failed to update route');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to update route');
    } finally {
      setTogglingId(null);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        ...(editingRecord ? { id: editingRecord.id } : {}),
        facility_code: values.facility_code?.trim(),
        centre_code: values.centre_code || null,
        host: values.host?.trim(),
        port: values.port,
        receiving_facility: values.receiving_facility?.trim() || null,
        // null clears the issuer on the backend; omit-to-keep isn't needed
        // since the form always carries the current value.
        patient_id_issuer: values.patient_id_issuer?.trim() || null,
        active: values.active,
        comments: values.comments?.trim() || null,
      };

      const res = await makePostCall('/hl7-forward-routes', payload);
      if (res?.data?.success) {
        message.success(res.data.message || `Route ${editingRecord ? 'updated' : 'created'} successfully`);
        closeModal();
        fetchRoutes();
      } else {
        throw new Error(res?.data?.message || 'Failed to save route');
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error.message || 'Failed to save route';
      // Duplicate facility code — surface it inline on the field.
      if (error?.response?.status === 409) {
        form.setFields([{ name: 'facility_code', errors: [serverMessage] }]);
      } else {
        message.error(serverMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  const filteredRoutes = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return (routesList.data || []).filter(route => {
      if (statusFilter === 'active' && !route.active) return false;
      if (statusFilter === 'inactive' && route.active) return false;
      if (search) {
        const centreName = centreNameByCode[route.centre_code] || '';
        const haystack = `${route.facility_code} ${route.centre_code || ''} ${centreName} ${route.host} ${route.receiving_facility || ''} ${route.patient_id_issuer || ''} ${route.comments || ''}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [routesList.data, statusFilter, searchText, centreNameByCode]);

  // Read-only until explicitly unlocked; only applies when the route being
  // edited already has an issuer (new routes / empty issuers edit freely).
  const issuerLocked = !!editingRecord?.patient_id_issuer && !issuerUnlocked;

  const columns = [
    {
      title: 'Facility Code',
      dataIndex: 'facility_code',
      key: 'facility_code',
      sorter: (a, b) => (a.facility_code || '').localeCompare(b.facility_code || ''),
      render: (code) => (
        <Space size={6}>
          <ApiOutlined style={{ color: '#0f766e' }} />
          <Tag>{code}</Tag>
        </Space>
      ),
    },
    {
      title: 'Centre',
      dataIndex: 'centre_code',
      key: 'centre_code',
      sorter: (a, b) => (centreNameByCode[a.centre_code] || a.centre_code || '').localeCompare(centreNameByCode[b.centre_code] || b.centre_code || ''),
      render: (code) => (code ? (centreNameByCode[code] || code) : <span className="mc-muted">—</span>),
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      sorter: (a, b) => (a.host || '').localeCompare(b.host || ''),
    },
    {
      title: 'Port',
      dataIndex: 'port',
      key: 'port',
    },
    {
      title: 'Receiving Facility',
      dataIndex: 'receiving_facility',
      key: 'receiving_facility',
      render: (val) => val || <span className="mc-muted">—</span>,
    },
    {
      title: `${getPatientIdLabel()} Issuer`,
      dataIndex: 'patient_id_issuer',
      key: 'patient_id_issuer',
      render: (val) => val || <span className="mc-muted">—</span>,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (active, record) => (
        <Switch
          checked={active}
          loading={togglingId === record.id}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      key: 'comments',
      ellipsis: true,
      render: (val) => val || <span className="mc-muted">—</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Edit HL7 Forward Route">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="masters-config-container">
      <div className="mc-titlebar">
        <div>
          <span className="mc-title">HL7 Forward Routes</span>
          <div className="mc-muted" style={{ fontSize: 13, marginTop: 2 }}>
            Route incoming HIS orders to each center's worklist server (dcm4chee)
          </div>
        </div>
        <Button type="primary" className="mc-add" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Route
        </Button>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <span>
            Orders whose facility code has no active route here are NOT forwarded to any
            worklist (check server logs for <code>[MWL] NOT FORWARDED</code>).
          </span>
        }
      />

      <div className="mc-toolbar">
        <Space size="middle" wrap>
          <Input
            placeholder="Search by facility code, centre or host"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 150 }}>
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Space>
      </div>

      {routesList.error && (
        <div className="mc-error">
          <p>{routesList.error}</p>
          <Button onClick={fetchRoutes}>Retry</Button>
        </div>
      )}

      <div className="mc-panel">
        <div className="mc-panel-head">
          <span>HL7 Forward Routes</span>
          <span className="mc-count">{filteredRoutes.length}</span>
        </div>
        <div className="mc-panel-body">
          <Table
            className="mc-table"
            columns={columns}
            dataSource={filteredRoutes}
            loading={routesList.loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} routes`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      <Modal
        className="masters-config-modal"
        title={editingRecord ? 'Edit HL7 Forward Route' : 'Add HL7 Forward Route'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="admin-form">
          <Form.Item
            label="Facility Code"
            name="facility_code"
            tooltip="Sending facility from the HIS (MSH-4), e.g. SMJ1, SEC1"
            normalize={(value) => value?.toUpperCase()}
            rules={[
              { required: true, message: 'Please enter the facility code' },
              { max: 50, message: 'Facility code must be 50 characters or less' },
            ]}
          >
            <Input placeholder="e.g. SMJ1" />
          </Form.Item>

          <Form.Item
            label="Centre"
            name="centre_code"
            tooltip="PACS centre this route belongs to"
          >
            <Select placeholder="Select a centre (optional)" allowClear showSearch optionFilterProp="children">
              {centres.map((c) => (
                <Option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Host"
            name="host"
            tooltip="dcm4chee HL7 (MLLP) listener host of the center"
            rules={[
              { required: true, message: 'Please enter the host' },
              { max: 100, message: 'Host must be 100 characters or less' },
            ]}
          >
            <Input placeholder="e.g. 192.152.80.161" />
          </Form.Item>

          <Form.Item
            label="Port"
            name="port"
            tooltip="dcm4chee HL7 port (default 2575)"
            rules={[
              { required: true, message: 'Please enter the port' },
              { type: 'number', min: 1, max: 65535, message: 'Port must be between 1 and 65535' },
            ]}
          >
            <InputNumber placeholder="2575" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Receiving Facility"
            name="receiving_facility"
            tooltip="Written to MSH-6 on the forwarded copy when the HIS leaves it empty; usually same as facility code"
            rules={[
              { max: 50, message: 'Receiving facility must be 50 characters or less' },
            ]}
          >
            <Input placeholder="e.g. SMJ1" />
          </Form.Item>

          <Form.Item
            label={`${getPatientIdLabel()} Issuer`}
            name="patient_id_issuer"
            tooltip="Stamped into PID-3.4 of the forwarded MWL order"
            extra={`Namespaces this centre's HIS ${getPatientIdLabel()} on the worklist server (dcm4chee).`}
            rules={[
              { max: 50, message: `${getPatientIdLabel()} issuer must be 50 characters or less` },
            ]}
          >
            <Input
              placeholder="e.g. YH_SMJ"
              disabled={issuerLocked}
              // addonAfter (not suffix) so the unlock button stays clickable
              // while the input itself is disabled.
              addonAfter={issuerLocked ? (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={handleUnlockIssuer}
                >
                  Change
                </Button>
              ) : undefined}
            />
          </Form.Item>

          <Form.Item
            label="Active"
            name="active"
            valuePropName="checked"
            tooltip="Inactive routes are ignored — those orders are logged and not forwarded"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item
            label="Comments"
            name="comments"
            rules={[
              { max: 200, message: 'Comments must be 200 characters or less' },
            ]}
          >
            <Input placeholder="Optional notes about this route" />
          </Form.Item>

          <Form.Item className="form-actions">
            <Button onClick={closeModal} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingRecord ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Hl7ForwardRoutes;
